import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
  Alert,
} from "@mui/material";
import { usePositions, usePermissionCatalog } from "../../../hooks/usePositions";
import PositionTable from "../../../components/PositionTable";
import AddPositionForm from "../../../components/AddPositionForm";
import { usePageContext } from "../../../context/PageContext";
import EmptyStatePositions from "../../../components/EmptyStatePositions";
import useSetup from "../../../hooks/useSetup";
import { useTranslation } from "react-i18next";

const Positions = () => {
  const { t } = useTranslation("settings");
  const { currentPage, setCurrentPage } = usePageContext();
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingSugestao, setLoadingSugestao] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { data, error, refetch, updateMutation, deleteMutation, isLoading } =
    usePositions(currentPage, pageSize, search);

  const { catalog, isLoading: catalogLoading } = usePermissionCatalog();
  const { generateRoles } = useSetup();

  useEffect(() => {
    if (error) setErrorMessage("Erro ao carregar cargos. Por favor, tente novamente.");
    else setErrorMessage(null);
  }, [error]);

  const positions = data?.positions || [];
  const isEmpty = !isLoading && positions.length === 0;

  const handleCriarCargosComIA = async () => {
    if (loadingSugestao) return;
    setLoadingSugestao(true);
    setErrorMessage(null);
    try {
      const response = await generateRoles({});
      const raw = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
        ? response.data
        : [];

      if (!raw.length) {
        setErrorMessage("Nenhuma sugestão de cargos retornada. Tente novamente.");
        return;
      }

      const seen = new Set();
      const suggested = raw.filter((item) => {
        const nome = (item?.cargo || item?.nome || "").trim();
        if (!nome) return false;
        const key = nome.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      if (!suggested.length) {
        setErrorMessage("Nenhuma sugestão de cargos válida retornada.");
        return;
      }

      for (let i = 0; i < suggested.length; i += 1) {
        const item = suggested[i];
        const nome = item?.cargo || item?.nome || `Cargo ${i + 1}`;
        const descricao = item?.descricao || item?.description || "";
        const scopes =
          Array.isArray(item?.scopes) && item.scopes.length
            ? item.scopes
            : Array.isArray(item?.permissoes)
            ? item.permissoes
            : [];

        // eslint-disable-next-line no-await-in-loop
        await updateMutation.mutateAsync({
          nome,
          descricao,
          status: 1,
          permissions: scopes,
          scopes,
        });
      }

      await refetch();
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.message || err?.message || "Erro ao criar cargos com IA."
      );
    } finally {
      setLoadingSugestao(false);
    }
  };

  // Handlers cargo
  const handleEdit = (position) => {
    setSelectedPosition(position);
    setErrorMessage(null);
  };

  const handleDelete = (id) => {
    setConfirmDelete(id);
    setErrorMessage(null);
  };

  const confirmDeletePosition = () => {
    if (!confirmDelete) return;
    setIsProcessing(true);
    deleteMutation.mutate(confirmDelete, {
      onSuccess: () => {
        setConfirmDelete(null);
        refetch();
      },
      onError: (err) =>
        setErrorMessage(err?.response?.data?.message || "Erro ao excluir o cargo."),
      onSettled: () => setIsProcessing(false),
    });
  };

  const toggleStatus = (row) => {
    setIsProcessing(true);
    updateMutation.mutate(
      { id: row.id, status: row.status ? 0 : 1 },
      {
        onSuccess: () => refetch(),
        onError: (err) =>
          setErrorMessage(err?.response?.data?.message || "Erro ao alterar status do cargo."),
        onSettled: () => setIsProcessing(false),
      }
    );
  };

  // Ações em massa
  const bulkActivate = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    try {
      await Promise.all(ids.map((id) => updateMutation.mutateAsync({ id, status: 1 })));
      await refetch();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Erro ao ativar cargos.");
    } finally {
      setIsProcessing(false);
    }
  };

  const bulkDeactivate = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    try {
      await Promise.all(ids.map((id) => updateMutation.mutateAsync({ id, status: 0 })));
      await refetch();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Erro ao desativar cargos.");
    } finally {
      setIsProcessing(false);
    }
  };

  const bulkDelete = async (ids = []) => {
    if (!ids.length) return;
    setIsProcessing(true);
    try {
      await Promise.all(ids.map((id) => deleteMutation.mutateAsync(id)));
      await refetch();
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Erro ao excluir cargos.");
    } finally {
      setIsProcessing(false);
    }
  };

  // Paginação / busca
  const handlePageChange = (newPage) => setCurrentPage(newPage + 1);
  const handlePageSizeChange = (newPageSize) => setPageSize(newPageSize);
  const handleSearch = (term) => {
    setSearch(term);
    refetch();
  };
  const handleCloseForm = () => {
    setSelectedPosition(null);
    setErrorMessage(null);
  };

  return (
    <Box sx={{ paddingTop: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4" gutterBottom>{t("positionsPage.headingRoles")}</Typography>
        <Button
          variant="contained"
          color="primary"
          fullWidth={isMobile}
          onClick={() => setSelectedPosition({})}
        >
          {t("positionsPage.addRole")}
        </Button>
      </Box>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {selectedPosition ? (
        <AddPositionForm
          data={selectedPosition}
          catalog={catalog}
          allPositions={positions}
          isLoading={isLoading || isProcessing || catalogLoading}
          onClose={handleCloseForm}
          onSubmit={(payload) => {
            setIsProcessing(true);
            updateMutation.mutate(payload, {
              onSuccess: () => { refetch(); handleCloseForm(); },
              onError: (err) =>
                setErrorMessage(err?.response?.data?.message || "Erro ao salvar o cargo."),
              onSettled: () => setIsProcessing(false),
            });
          }}
        />
      ) : isEmpty ? (
        <EmptyStatePositions
          description="Os cargos definem quais áreas do sistema cada perfil pode acessar e quais ações pode realizar — por exemplo: visualizar apenas o atendimento, editar configurações ou gerenciar campanhas. Isso garante segurança e organização no uso da plataforma."
          onPrimaryClick={handleCriarCargosComIA}
          onSecondaryClick={() => setSelectedPosition({})}
          primaryLoading={loadingSugestao}
        />
      ) : (
        <PositionTable
          positions={positions}
          rowCount={data?.pagination?.total || 0}
          pageSize={pageSize}
          page={currentPage - 1}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSearch={handleSearch}
          isLoading={isLoading || isProcessing}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onToggleStatus={toggleStatus}
          onBulkActivate={bulkActivate}
          onBulkDeactivate={bulkDeactivate}
          onBulkDelete={bulkDelete}
        />
      )}

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Confirmação de Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            {isProcessing ? "Processando exclusão..." : "Tem certeza de que deseja excluir este cargo?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!isProcessing && (
            <>
              <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
              <Button color="error" onClick={confirmDeletePosition}>Excluir</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Positions;
